import React, { useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Wallet, Receipt, Gift, ScanLine, LogOut, User } from "lucide-react";
import { useAuth } from "../../lib/auth";

const links = [
    { to: "/account", label: "Overview", icon: Home, end: true },
    { to: "/account/wallet", label: "Wallet", icon: Wallet },
    { to: "/account/vouchers", label: "My Vouchers", icon: Gift },
    { to: "/account/bills", label: "Bills", icon: Receipt },
    { to: "/account/scan", label: "Scan Bill", icon: ScanLine },
    { to: "/account/profile", label: "Profile", icon: User },
];

export default function AccountShell() {
    const { customer, loading, logout } = useAuth();
    const navigate = useNavigate();
    const loc = useLocation();

    useEffect(() => {
        if (!loading && !customer) {
            navigate(`/login?next=${encodeURIComponent(loc.pathname)}`, { replace: true });
        }
    }, [customer, loading, navigate, loc.pathname]);

    if (loading || !customer) return <div className="p-10 text-center text-[#6b7280]">Loading…</div>;

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid md:grid-cols-[240px_1fr] gap-6">
            <aside className="hidden md:flex flex-col fx-card p-3 self-start sticky top-24">
                <div className="px-3 py-2 flex items-center gap-3 border-b border-[rgba(11,20,55,0.05)] pb-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ccff00] to-[#ffd700] text-black flex items-center justify-center font-bold">
                        {(customer.name || "F")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{customer.name || customer.mobile}</p>
                        <p className="text-[10px] text-[#9ca3af]">{customer.tier} • {customer.loyalty_id}</p>
                    </div>
                </div>
                {links.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all ${
                                isActive
                                    ? "bg-[#c9a959]/15 text-[#c9a959] border border-[#c9a959]/30"
                                    : "text-[#374151] hover:bg-[#f9fafb]"
                            }`
                        }
                    >
                        <l.icon size={16} /> {l.label}
                    </NavLink>
                ))}
                <button
                    data-testid="account-logout-btn"
                    onClick={() => {
                        logout();
                        navigate("/");
                    }}
                    className="mt-2 flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-[#6b7280] hover:text-[#ff4d4d] hover:bg-[#f9fafb]"
                >
                    <LogOut size={16} /> Sign out
                </button>
            </aside>

            {/* Mobile horizontal nav */}
            <nav className="md:hidden flex gap-1 overflow-x-auto no-scrollbar -mx-4 px-4 mb-2">
                {links.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        className={({ isActive }) =>
                            `shrink-0 px-3 py-1.5 rounded-full text-xs ${
                                isActive ? "bg-[#c9a959] text-black font-semibold" : "bg-[#f9fafb] text-[#374151] border border-[rgba(11,20,55,0.10)]"
                            }`
                        }
                    >
                        {l.label}
                    </NavLink>
                ))}
            </nav>

            <main className="min-w-0">
                <Outlet />
            </main>
        </div>
    );
}
